import { AnalyticIdentifier } from '@common/neo.tax';
import { colors, DefaultComponentProps, ModalHeader, RadioWithIconGroup, Stack } from '@ui-components/neo.tax';
import React, { useState } from 'react';

import { AccountingProvider, PayrollProvider, ProviderType, TicketingProvider } from '@neo.tax/shared/graphql';
import { useAnalyticId } from '@neo.tax/shared/state';

import { useAccountingProviderCards, usePayrollProviderCards, useTicketingProviderCards } from './hooks';
import { StyledButton, StyledModalActions, StyledPlusCircle, StyledSubTitle } from './select-providers.styled';

/**
 * we have to use a provider + type since the provider enums are not unique between the different types
 */
export type DeterministicProvider = {
  provider: AccountingProvider | PayrollProvider | TicketingProvider;
  providerType: ProviderType;
};

type SelectProvidersProps = DefaultComponentProps & {
  close: () => void;
  showProvidersInTypes: Set<ProviderType>;
  payrollProvidersAlreadyConnected?: Set<PayrollProvider>;
  accountingProvidersAlreadyConnected?: Set<AccountingProvider>;
  ticketingProvidersAlreadyConnected?: Set<TicketingProvider>;
  selectProvider: (selectedProvider: DeterministicProvider | null) => void;
};

export const SelectProviders = ({
  close,
  selectProvider,
  payrollProvidersAlreadyConnected = new Set(),
  accountingProvidersAlreadyConnected = new Set(),
  ticketingProvidersAlreadyConnected = new Set(),
  showProvidersInTypes,
}: SelectProvidersProps): React.ReactElement => {
  const continueConnectAccountId = useAnalyticId(AnalyticIdentifier.CONTINUE_CONNECT_ACCOUNT);
  const cancelConnectAccountId = useAnalyticId(AnalyticIdentifier.CANCEL_CONNECT_ACCOUNT);

  const [selectedProvider, setSelectedProvider] = useState<DeterministicProvider>();

  const payrollProviderCards = usePayrollProviderCards({
    enabled: showProvidersInTypes.has(ProviderType.Payroll),
    connectedProviders: payrollProvidersAlreadyConnected,
  });
  const accountingProviderCards = useAccountingProviderCards({
    enabled: showProvidersInTypes.has(ProviderType.Accounting),
    connectedProviders: accountingProvidersAlreadyConnected,
  });
  const ticketingProviderCards = useTicketingProviderCards({
    enabled: showProvidersInTypes.has(ProviderType.Ticketing),
    connectedProviders: ticketingProvidersAlreadyConnected,
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!selectedProvider) {
      return;
    }
    selectProvider(selectedProvider);
  };

  return (
    <Stack as="form" onSubmit={handleSubmit} direction="column" space={6}>
      <ModalHeader
        copy="Select the account you want to securely connect."
        icon={<StyledPlusCircle style={{ color: colors.blue[600] }} />}
        iconBgColor={colors.white}
        title="Connect New Account"
      />
      {payrollProviderCards.length > 0 && (
        <div>
          <StyledSubTitle as="h4" fontSize="sm" fontWeight="medium">
            Payroll
          </StyledSubTitle>
          <RadioWithIconGroup<PayrollProvider>
            radioWithIcons={payrollProviderCards}
            currentValue={selectedProvider?.providerType === ProviderType.Payroll ? selectedProvider.provider : ''}
            onChange={(value: PayrollProvider): void =>
              setSelectedProvider({ provider: value, providerType: ProviderType.Payroll })
            }
          />
        </div>
      )}
      {accountingProviderCards.length > 0 && (
        <div>
          <StyledSubTitle as="h4" fontSize="sm" fontWeight="medium">
            Books
          </StyledSubTitle>
          <RadioWithIconGroup<AccountingProvider>
            radioWithIcons={accountingProviderCards}
            currentValue={selectedProvider?.providerType === ProviderType.Accounting ? selectedProvider.provider : ''}
            onChange={(value: AccountingProvider): void =>
              setSelectedProvider({ provider: value, providerType: ProviderType.Accounting })
            }
          />
        </div>
      )}
      {ticketingProviderCards.length > 0 && (
        <div>
          <StyledSubTitle as="h4" fontSize="sm" fontWeight="medium">
            Ticketing
          </StyledSubTitle>
          <RadioWithIconGroup<TicketingProvider>
            radioWithIcons={ticketingProviderCards}
            currentValue={selectedProvider?.providerType === ProviderType.Ticketing ? selectedProvider.provider : ''}
            onChange={(value: TicketingProvider): void =>
              setSelectedProvider({ provider: value, providerType: ProviderType.Ticketing })
            }
          />
        </div>
      )}
      <StyledModalActions hasTopBorder={false}>
        <StyledButton variant="white" rounded="regular" size="base" onClick={close} id={cancelConnectAccountId}>
          Cancel
        </StyledButton>
        <StyledButton
          variant="primary"
          rounded="regular"
          size="base"
          disabled={!selectedProvider}
          type="submit"
          id={continueConnectAccountId}
        >
          Connect
        </StyledButton>
      </StyledModalActions>
    </Stack>
  );
};
